import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Box, CircularProgress, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";

export default function CanliKamera({ src, height = 420 }) {
  const videoRef = useRef(null);
  const [durum, setDurum] = useState("baglaniyor");

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let iptal = false;
    let hls = null;
    setDurum("baglaniyor");

    // Dış servis yayını istek anında başlattığı için ilk saniyelerde
    // playlist boş dönebiliyor. Oynatıcıyı bağlamadan önce playlist'te
    // en az bir segment oluşmasını bekliyoruz.
    // Dönüş: "hazir" | "kapali" | "zaman_asimi"
    const playlistHazirMi = async () => {
      for (let deneme = 0; deneme < 8; deneme++) {
        if (iptal) return "zaman_asimi";
        try {
          const res = await fetch(src, {
            credentials: "include",
            cache: "no-store",
          });

          // 503/500 = kamera veya yayın servisi kapalı, beklemenin anlamı yok
          if (res.status === 503 || res.status === 500) return "kapali";

          if (res.ok) {
            const metin = await res.text();
            if (/\.(ts|m4s|mp4|aac)/i.test(metin)) return "hazir";
          }
        } catch (_) {
          // ağ hatası — tekrar denenecek
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      return "zaman_asimi";
    };

    const baslat = async () => {
      const sonuc = await playlistHazirMi();
      if (iptal) return;

      if (sonuc === "kapali") {
        setDurum("kapali");
        return;
      }
      if (sonuc !== "hazir") {
        setDurum("hata");
        return;
      }

      // Safari / iOS — yerleşik HLS desteği
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
        video.oncanplay = () => setDurum("hazir");
        video.onerror = () => setDurum("hata");
        return;
      }

      if (!Hls.isSupported()) {
        setDurum("hata");
        return;
      }

      hls = new Hls({
        lowLatencyMode: false,
        liveSyncDurationCount: 1,
        liveMaxLatencyDurationCount: 10,
        liveDurationInfinity: true,
        manifestLoadingTimeOut: 30000,
        manifestLoadingMaxRetry: 10,
        manifestLoadingRetryDelay: 1500,
        levelLoadingTimeOut: 30000,
        levelLoadingMaxRetry: 10,
        levelLoadingRetryDelay: 1500,
        fragLoadingTimeOut: 40000,
        fragLoadingMaxRetry: 10,
        xhrSetup: (xhr) => {
          xhr.withCredentials = true;
        },
      });

      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => setDurum("hazir"));

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;

        // Yayın oynarken servis düşerse tekrar denemek yükü artırır
        const kod = data.response?.code;
        if (kod === 503 || kod === 500) {
          hls.destroy();
          setDurum("kapali");
          return;
        }

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          hls.destroy();
          setDurum("hata");
        }
      });
    };

    baslat();

    return () => {
      iptal = true;
      if (hls) hls.destroy();
      video.src = "";
    };
  }, [src]);

  const mesaj = {
    baglaniyor: "Yayın başlatılıyor…",
    kapali: "Kamera şu anda yayın yapmıyor",
    hata: "Yayın alınamadı",
  }[durum];

  const altMesaj =
    durum === "baglaniyor"
      ? "Bu işlem birkaç saniye sürebilir"
      : durum === "kapali"
      ? "Kamera kapalı olabilir veya bağlantı kurulamıyor"
      : "Lütfen daha sonra tekrar deneyin";

return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "#000",
        borderRadius: 3,
        overflow: "hidden",
        width: "100%",
        aspectRatio: "16 / 9",
        maxHeight: "75vh",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        controls
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />

      {durum !== "hazir" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.55)",
            px: 3,
            textAlign: "center",
          }}
        >
          {durum === "baglaniyor" ? (
            <CircularProgress size={34} sx={{ color: "#fff" }} />
          ) : durum === "kapali" ? (
            <VideocamOffOutlinedIcon sx={{ fontSize: 42, color: "#94A3B8" }} />
          ) : (
            <ErrorOutlineIcon sx={{ fontSize: 40, color: "#EF4444" }} />
          )}

          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{mesaj}</Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{altMesaj}</Typography>
        </Box>
      )}
    </Box>
  );
}